/**
 * BeBobi product spotlight widget.
 * Ports the size-fitter + bundle/add-on price builder + cross-sell quick-add
 * from the source React components (SizeFitter / BundleUpsell / CrossSell / store.tsx)
 * to a vanilla custom element that integrates with Horizon's AJAX cart + drawer.
 */
import { CartAddEvent } from '@theme/events';
import { BB_GROUP, BB_ROLE, BB_KIND, BB_SLOT, BB_PER, BB_SOURCE } from '@theme/bebobi-cart-bundles';
import { matchCar } from '@theme/bebobi-car-map';

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* Minimal Shopify money formatter driven by the shop's money_format string. */
function formatMoney(cents, format) {
  const value = (Number(cents) || 0) / 100;
  const group = (num, decimals, decSep, thouSep) => {
    const fixed = Math.abs(num).toFixed(decimals);
    let [int, dec] = fixed.split('.');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    return dec ? int + decSep + dec : int;
  };
  const fmt = format || '{{amount}}';
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
    switch (name) {
      case 'amount': return group(value, 2, '.', ',');
      case 'amount_no_decimals': return group(value, 0, '.', ',');
      case 'amount_with_comma_separator': return group(value, 2, ',', '.');
      case 'amount_no_decimals_with_comma_separator': return group(value, 0, ',', '.');
      case 'amount_with_space_separator': return group(value, 2, ',', ' ');
      case 'amount_no_decimals_with_space_separator': return group(value, 0, '.', ' ');
      case 'amount_with_apostrophe_separator': return group(value, 2, '.', "'");
      default: return group(value, 2, '.', ',');
    }
  });
}

const BB_EN = (document.documentElement.lang || 'pl').toLowerCase().startsWith('en');

/* Bundle line-item property names come from bebobi-cart-bundles.js, which owns
   the reconciler. The parent label is deliberately visible: the customer has to
   understand why there is a second line. */
const BB_PARENT_LABEL = BB_EN ? 'Add-on to' : 'Dodatek do';
/* Label shown on the add button (and swatch tooltips) when the selected variant
   cannot be bought - Shopify would answer /cart/add with 422 anyway. */
const BB_UNAVAILABLE_LABEL = BB_EN ? 'Unavailable' : 'Niedostępny';

function bbGroupId() {
  const rnd = (globalThis.crypto && globalThis.crypto.randomUUID)
    ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `bb_${rnd}`;
}

class BebobiSpotlight extends HTMLElement {
  connectedCallback() {
    this.discount = parseInt(this.dataset.discount || '0', 10);
    this.baseCents = parseInt(this.dataset.baseCents || '0', 10);
    this.mainVariant = this.dataset.mainVariant || '';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    // pakiet: cena materacyka i podłogi przy zakupie razem (0 = brak pakietu)
    this.bundleMattressCents = parseInt(this.dataset.bundleMattress || '0', 10);
    this.bundleFloorCents = parseInt(this.dataset.bundleFloor || '0', 10);
    this.bundleFloorRegularCents = parseInt(this.dataset.bundleFloorRegular || '0', 10);
    this.selectedVariant = this.mainVariant;

    this.totalEls = [...this.querySelectorAll('[data-spotlight-total], [data-spotlight-btn-total]')];
    this.sizeButtons = [...this.querySelectorAll('[data-spotlight-sizes] .bebobi-size')];
    this.carInput = this.querySelector('[data-spotlight-car]');
    this.note = this.querySelector('[data-spotlight-note]');
    this.noteText = this.querySelector('[data-spotlight-note-text]');
    this.addons = [...this.querySelectorAll('[data-spotlight-addon]')];
    this.addBtn = this.querySelector('[data-spotlight-add]');
    this.addBtnLabel = this.querySelector('[data-spotlight-btn-label]');
    this.preorderBox = this.querySelector('[data-bb-preorder-box]');
    this.selectedAvailable = true;
    this.selectedPreorder = false;
    /* Rozmiar/podłoga "wybrane" dopiero przez klienta (klik, dobór po aucie, link ?variant= -> Liquid
       data-size-chosen). Rozmiar ustawiony domyślnie przy ładowaniu (pierwszy dostępny wariant) nie
       zawęża przekreśleń kolorów - inaczej na KUKO+ przy domyślnym S przekreślone były kolory
       zamawialne w M/L (Brązowa, Czarna, Szara). */
    this.sizeChosen = this.dataset.sizeChosen === 'true';
    this.floorChosen = this.dataset.sizeChosen === 'true';
    // notka dostawy pod CTA: stan per wariant przychodzi z Liquid (pole "delivery" w JSON wariantów)
    this.shipNote = this.querySelector('[data-spotlight-ship]');
    this.selectedDelivery = 'stock';
    this.expressWrap = this.querySelector('[data-spotlight-express]');
    this.expressId = this.querySelector('[data-spotlight-express-id]');
    this.crossBtns = [...this.querySelectorAll('[data-spotlight-cross]')];
    this.mainImg = this.querySelector('.bebobi-spotlight__img');

    // pełny konfigurator: warianty produktu (kolor × rozmiar) z JSON-a
    this.variants = [];
    try {
      const raw = this.querySelector('[data-spotlight-variants]');
      if (raw) this.variants = JSON.parse(raw.textContent);
    } catch (_) { this.variants = []; }
    this.swatches = [...this.querySelectorAll('[data-spotlight-color]')];
    this.colorNameEl = this.querySelector('[data-spotlight-color-name]');
    this.floorBtns = [...this.querySelectorAll('[data-spotlight-floors] [data-floor]')];
    // materac wymagany do twardej podłogi (KUKO+): warianty produktu-dodatku
    this.floorAddon = [];
    try {
      const fa = this.querySelector('[data-spotlight-flooraddon]');
      if (fa) this.floorAddon = JSON.parse(fa.textContent);
    } catch (_) { this.floorAddon = []; }
    this.floorAddonSel = null;
    this.floorAddonColor = null; // wybór klienta; null = automatycznie wg koloru maty
    this.floorAddonRow = this.querySelector('[data-floor-addon]');
    this.floorAddonText = this.querySelector('[data-floor-addon-text]');
    this.floorAddonPick = this.querySelector('[data-floor-addon-pick]');
    this.floorAddonColors = this.querySelector('[data-floor-addon-colors]');
    const initial = this.variants.find((v) => String(v.id) === String(this.mainVariant)) || this.variants[0];
    this.selectedColor = initial ? initial.kolor : '';
    this.selectedSize = initial ? initial.rozmiar : '';
    this.selectedFloor = initial ? initial.podloga || '' : '';
    this.selectedAvailable = initial ? initial.available !== false : true;
    this.selectedPreorder = !!(initial && initial.preorder);
    this.selectedDelivery = initial ? (initial.delivery || 'stock') : 'stock';

    this.sizeButtons.forEach((b) => b.addEventListener('click', () => this.pickSize(b)));
    this.swatches.forEach((s) => s.addEventListener('click', () => this.pickColor(s)));
    this.floorBtns.forEach((f) => f.addEventListener('click', () => this.pickFloor(f)));

    // mini-galeria: miniatury + strzałki na zdjęciu
    this.thumbs = [...this.querySelectorAll('[data-spotlight-thumbs] .bebobi-spotlight__thumb')];
    this.galleryIndex = -1; // -1 = zdjęcie wariantu
    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.showGallery(i)));
    this.querySelector('[data-spotlight-prev]')?.addEventListener('click', () => this.stepGallery(-1));
    this.querySelector('[data-spotlight-next]')?.addEventListener('click', () => this.stepGallery(1));
    // swipe palcem po zdjęciu (mobile) — pasywnie, żeby nie blokować pionowego scrolla strony
    const media = this.querySelector('.bebobi-spotlight__media');
    if (media) {
      let x0 = 0, y0 = 0;
      media.addEventListener('touchstart', (e) => {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });
      media.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepGallery(dx < 0 ? 1 : -1);
      }, { passive: true });
    }
    this.carInput?.addEventListener('input', () => this.onCar());
    this.addons.forEach((a) => a.addEventListener('click', () => this.toggleAddon(a)));
    this.addBtn?.addEventListener('click', () => this.addBundle());
    this.crossBtns.forEach((b) => b.addEventListener('click', () => this.addCross(b)));

    // rigid floor sold as its own line: component variants + toggle/select
    this.floorComponentId = this.dataset.floorComponent || '';
    this.floorComps = [];
    try {
      const fc = this.querySelector('[data-spotlight-floorcomp]');
      if (fc) this.floorComps = JSON.parse(fc.textContent);
    } catch (_) { this.floorComps = []; }
    /* The hidden <select> stays the single source of truth for the choice
       (floorWanted reads .value); the customer drives it with the Tak/Nie
       tiles, which have a real selected state (.is-selected). */
    this.floorSelect = this.querySelector('[data-spotlight-floor-select]');
    this.floorNote = this.querySelector('[data-floor-note]');
    this.floorSelect?.addEventListener('change', () => this.syncFloorSelect());
    this.floorChoiceBtns = [...this.querySelectorAll('[data-floor-choice]')];
    this.floorChoiceBtns.forEach((b) => b.addEventListener('click', () => {
      if (!this.floorSelect) return;
      this.floorSelect.value = b.dataset.floorChoice;
      this.syncFloorSelect();
    }));

    this.syncSelection({ skipImage: true });
    this.syncFloorSelect();
    this.recalc();
  }

  /* znajdź wariant dla wybranego koloru+rozmiaru+podłogi i zsynchronizuj UI */
  syncSelection(opts = {}) {
    if (!this.variants.length) return;
    const eq = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
    /* split mode: the floor is a separate cart line, the mat resolves by
       colour x size only (the Podloga option is gone from the product) */
    const floorSplit = this.floorComps.length > 0 || !!this.floorComponentId;
    let match = this.variants.find((v) =>
      (!this.selectedColor || eq(v.kolor, this.selectedColor)) &&
      (!this.selectedSize || eq(v.rozmiar, this.selectedSize)) &&
      (floorSplit || !this.selectedFloor || eq(v.podloga, this.selectedFloor))
    );
    // brak pełnej kombinacji → spróbuj bez podłogi, potem sam kolor
    if (!match && this.selectedColor) {
      match = this.variants.find((v) => eq(v.kolor, this.selectedColor) && (!this.selectedSize || eq(v.rozmiar, this.selectedSize)));
      if (match) this.selectedFloor = match.podloga || '';
    }
    if (!match && this.selectedColor) {
      match = this.variants.find((v) => eq(v.kolor, this.selectedColor));
      if (match) { this.selectedSize = match.rozmiar; this.selectedFloor = match.podloga || ''; }
    }
    if (!match) match = this.variants[0];
    /* rozmiar tylko domyślny (klient go nie wybrał), a w nim ten kolor jest niezamawialny:
       przejdź na pierwszy zamawialny wariant tego koloru (stan, bez śledzenia lub przedsprzedaż),
       zamiast zostawić "Niedostępny" przy kolorze, który swatch pokazuje jako dostępny */
    if (!this.sizeChosen && match.available === false && this.selectedColor) {
      const alt = this.variants.find((v) => eq(v.kolor, this.selectedColor)
        && (floorSplit || !this.floorChosen || !this.selectedFloor || eq(v.podloga, this.selectedFloor))
        && v.available !== false);
      if (alt) {
        match = alt;
        this.selectedSize = alt.rozmiar;
        if (!floorSplit) this.selectedFloor = alt.podloga || '';
      }
    }
    this.selectedVariant = String(match.id);
    this.baseCents = match.price;
    /* stock of the resolved variant drives the add button + express wallet (syncBuyState via recalc) */
    this.selectedAvailable = match.available !== false;
    /* przedsprzedaż (stan <= 0 + continue) liczy Liquid, ta sama reguła co blocks/buy-buttons.liquid */
    this.selectedPreorder = !!match.preorder;
    this.selectedDelivery = match.delivery || (match.available !== false ? 'stock' : 'soldout');
    this.swatches.forEach((s) => s.classList.toggle('is-selected', s.dataset.spotlightColor === this.selectedColor));
    if (this.colorNameEl) this.colorNameEl.textContent = this.selectedColor || '';
    this.sizeButtons.forEach((b) => b.classList.toggle('is-selected', (b.dataset.key || '').toUpperCase() === String(this.selectedSize).toUpperCase()));
    this.syncSizes(eq);
    this.syncSwatches(eq);
    this.syncFloors(eq);
    this.syncFloorAddon(eq);
    this.syncFloorGate();
    if (!opts.skipImage && this.mainImg && match.image) {
      this.mainImg.src = match.image;
      if (this.mainImg.srcset) this.mainImg.removeAttribute('srcset');
      this.galleryIndex = -1;
      if (this.thumbs) this.thumbs.forEach((t) => t.classList.remove('is-active'));
    }
    this.recalc();
  }

  pickColor(btn) {
    this.selectedColor = btn.dataset.spotlightColor || '';
    this.syncSelection();
  }

  /* przekreśl rozmiary niedostępne w wybranym kolorze (brak kombinacji lub brak stanu) */
  syncSizes(eq) {
    if (!this.variants.length) return;
    this.sizeButtons.forEach((b) => {
      const key = (b.dataset.key || '').toUpperCase();
      const v = this.variants.find((x) =>
        (!this.selectedColor || eq(x.kolor, this.selectedColor)) && eq(x.rozmiar, key)
      );
      const ok = !!v && v.available !== false;
      b.classList.toggle('is-unavailable', !ok);
      b.disabled = !ok;
    });
  }

  /* Swatche koloru nigdy nie są przekreślone (Aga 10/2026), także gdy kolor jest wyprzedany we
     wszystkich rozmiarach — niedostępność pokazują kafle rozmiarów (syncSizes: rozmiary niezamawialne
     w WYBRANYM kolorze) i przycisk "Niedostępny" (syncBuyState). Tu tylko sprzątanie klasy/etykiety. */
  syncSwatches() {
    this.swatches.forEach((s) => {
      const color = s.dataset.spotlightColor || '';
      s.classList.remove('is-unavailable');
      s.setAttribute('aria-label', color);
      s.title = color;
    });
  }

  /* przycisk "Dodaj do koszyka" + portfel ekspresowy podążają za stanem wybranego wariantu:
     niedostępny = disabled + "Niedostępny" zamiast etykiety i ceny; przedsprzedaż = "Zamów
     w przedsprzedaży"; dostępny = przywrócenie oryginalnej etykiety (cena i przekreślenie
     wracają przez recalc) */
  syncBuyState() {
    if (!this.addBtn) return;
    const ok = this.selectedAvailable !== false;
    this.addBtn.disabled = !ok;
    this.addBtn.classList.toggle('is-unavailable', !ok);
    if (this.addBtnLabel) {
      const add = (this.selectedPreorder && this.addBtn.dataset.labelPreorder) || this.addBtn.dataset.labelAdd || '';
      this.addBtnLabel.textContent = ok ? `${add} ·` : (this.addBtn.dataset.labelUnavailable || BB_UNAVAILABLE_LABEL);
    }
    /* ramka przedsprzedaży nad przyciskiem: tylko dla dostępnego wariantu w przedsprzedaży */
    if (this.preorderBox) this.preorderBox.hidden = !(ok && this.selectedPreorder);
    const totalEl = this.addBtn.querySelector('[data-spotlight-btn-total]');
    if (totalEl) totalEl.hidden = !ok;
    if (!ok) {
      const cmpEl = this.addBtn.querySelector('[data-spotlight-btn-compare]');
      if (cmpEl) cmpEl.hidden = true;
    }
  }

  /* Notka dostawy pod CTA podąża za stanem wybranego wariantu. Reguły liczy Liquid
     (snippets/bebobi-delivery-state, ten sam co na PDP) i przekazuje je w polu "delivery"
     wariantu; teksty per stan siedzą w data-ship-* na <p data-spotlight-ship>. */
  syncShipNote() {
    if (!this.shipNote) return;
    const state = this.selectedDelivery || 'stock';
    const text = this.shipNote.dataset['ship' + state.charAt(0).toUpperCase() + state.slice(1)];
    if (text !== undefined) this.shipNote.textContent = text;
    this.shipNote.dataset.deliveryState = state;
  }

  /* domyślny kolor materaca wg koloru maty (Beżowa → Mono beż itd.) */
  defaultAddonColor(inSize) {
    const base = String(this.selectedColor || '').toLowerCase().replace(/owa$|a$/, '');
    const colorMap = { 'beż': 'mono beż', 'brąz': 'mono brąz', 'czarn': 'mono czarny', 'szar': 'mono szary' };
    for (const k in colorMap) {
      if (base.indexOf(k) === 0) {
        const hit = inSize.find((v) => String(v.kolor).toLowerCase() === colorMap[k]);
        if (hit) return hit.kolor;
      }
    }
    const mono = inSize.find((v) => /mono/i.test(v.kolor));
    return mono ? mono.kolor : (inSize[0] ? inSize[0].kolor : null);
  }

  /* twarda podłoga = zestaw z materacem: wybór koloru materaca ze wszystkich wzorów */
  syncFloorAddon(eq) {
    if (!this.floorAddonRow) return;
    eq = eq || ((a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase());
    const wanted = (this.floorComps.length || this.floorComponentId)
      ? this.floorWanted()
      : /twarda/i.test(String(this.selectedFloor || ''));
    const active = wanted && this.floorAddon.length;
    if (!active) {
      this.floorAddonSel = null;
      this.floorAddonRow.hidden = true;
      if (this.floorAddonPick) this.floorAddonPick.hidden = true;
      this.recalc();
      return;
    }
    const inSize = this.bbMattressCandidates(eq) || [];
    if (!inSize.length) {
      this.floorAddonSel = null;
      this.floorAddonRow.hidden = true;
      if (this.floorAddonPick) this.floorAddonPick.hidden = true;
      this.recalc();
      return;
    }
    // wybór klienta, o ile wciąż istnieje w tym rozmiarze; inaczej default wg maty
    let color = this.floorAddonColor && inSize.some((v) => eq(v.kolor, this.floorAddonColor))
      ? this.floorAddonColor
      : this.defaultAddonColor(inSize);
    const pick = inSize.find((v) => eq(v.kolor, color)) || inSize[0];
    this.floorAddonSel = pick;
    // pigułki kolorów materaca (wszystkie wzory dostępne w rozmiarze)
    if (this.floorAddonColors) {
      this.floorAddonColors.innerHTML = '';
      inSize.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bebobi-floor__color' + (eq(v.kolor, pick.kolor) ? ' is-selected' : '');
        b.title = v.kolor;
        b.setAttribute('aria-label', v.kolor);
        if (v.image) {
          const img = document.createElement('img');
          img.src = v.image;
          img.alt = v.kolor;
          img.loading = 'lazy';
          img.width = 46;
          img.height = 46;
          b.appendChild(img);
        } else {
          b.textContent = v.kolor;
        }
        b.addEventListener('click', () => {
          this.floorAddonColor = v.kolor;
          this.syncFloorAddon(eq);
        });
        this.floorAddonColors.appendChild(b);
      });
      this.floorAddonPick.hidden = false;
    }
    const eff = this.bundleMattressCents && this.bundleMattressCents < pick.price ? this.bundleMattressCents : pick.price;
    const was = eff < pick.price
      ? (BB_EN ? ` (instead of ${Math.round(pick.price / 100)} zł)` : ` (zamiast ${Math.round(pick.price / 100)} zł)`)
      : '';
    const price = ` · +${Math.round(eff / 100)} zł`;
    /* the saving of THIS pair, from compare_at - price of the two selected
       W komplecie variants (mattress + size-matched floor) */
    const floorPick = this.floorCompPick();
    let savingCents = Math.max(0, (pick.compare || 0) - pick.price);
    if (floorPick) savingCents += Math.max(0, (floorPick.compare || 0) - floorPick.price);
    const saving = savingCents > 0
      ? (BB_EN
        ? ` · you save ${formatMoney(savingCents, this.moneyFormat)}`
        : ` · oszczędzasz ${formatMoney(savingCents, this.moneyFormat)}`)
      : '';
    this.floorAddonText.innerHTML = BB_EN
      ? `Mattress <b>${escapeHtml(pick.kolor)} (${escapeHtml(pick.rozmiar)})</b>${price} in the set${was} — the rigid board slides into the mattress.${saving}`
      : `Materacyk <b>${escapeHtml(pick.kolor)} (${escapeHtml(pick.rozmiar)})</b>${price} w komplecie${was} — sztywna płyta wsuwa się do materacyka.${saving}`;
    this.floorAddonRow.hidden = false;
    this.recalc();
  }

  showGallery(i) {
    if (!this.thumbs.length) return;
    const n = this.thumbs.length;
    this.galleryIndex = ((i % n) + n) % n;
    const t = this.thumbs[this.galleryIndex];
    if (this.mainImg && t.dataset.full) {
      this.mainImg.src = t.dataset.full;
      if (this.mainImg.srcset) this.mainImg.removeAttribute('srcset');
    }
    this.thumbs.forEach((x, k) => x.classList.toggle('is-active', k === this.galleryIndex));
    t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  stepGallery(dir) {
    this.showGallery(this.galleryIndex < 0 ? (dir > 0 ? 0 : -1) : this.galleryIndex + dir);
  }

  pickFloor(btn) {
    this.selectedFloor = btn.dataset.floor || '';
    this.floorChosen = true;
    this.syncSelection({ skipImage: true });
  }

  /* zaznaczenie + dopłaty na przyciskach podłogi (delta vs najtańsza wersja w tym kolorze+rozmiarze) */
  currentFloorDelta(eq) {
    const combo = this.variants.filter((v) =>
      (!this.selectedColor || eq(v.kolor, this.selectedColor)) &&
      (!this.selectedSize || eq(v.rozmiar, this.selectedSize)) && v.podloga
    );
    if (!combo.length) return 0;
    const t = combo.filter((v) => /twarda/i.test(v.podloga)).map((v) => v.price);
    const st = combo.filter((v) => !/twarda/i.test(v.podloga)).map((v) => v.price);
    if (!t.length || !st.length) return 0;
    return Math.min(...t) - Math.min(...st);
  }

  syncFloors(eq) {
    if (!this.floorBtns.length) return;
    const inCombo = this.variants.filter((v) =>
      (!this.selectedColor || eq(v.kolor, this.selectedColor)) &&
      (!this.selectedSize || eq(v.rozmiar, this.selectedSize)) && v.podloga
    );
    const base = inCombo.length ? Math.min(...inCombo.map((v) => v.price)) : 0;
    // dopłata na kaflu "Tak, z materacem" zawiera też cenę materaca
    let addonExtra = 0;
    if (this.floorAddon.length) {
      const sz = String(this.selectedSize || '').toUpperCase();
      const wantSize = sz === 'S' ? 'S' : 'M/L';
      const prices = this.floorAddon.filter((v) => eq(v.rozmiar, wantSize) && v.available !== false).map((v) => v.price);
      if (prices.length) addonExtra = Math.min(...prices);
    }
    this.floorBtns.forEach((b) => {
      b.classList.toggle('is-selected', eq(b.dataset.floor, this.selectedFloor));
      const v = inCombo.find((x) => eq(x.podloga, b.dataset.floor));
      const el = b.querySelector('[data-floor-delta]');
      if (el) {
        let delta = v ? v.price - base : 0;
        if (delta > 0 && /twarda/i.test(b.dataset.floor || '')) {
          if (this.bundleMattressCents && this.floorAddon.length) {
            delta = 0; /* pakiet: kafel bez dopłaty — cena widoczna przy sumie */
          } else {
            delta += addonExtra;
          }
        }
        el.textContent = delta > 0 ? ` +${Math.round(delta / 100)} zł` : '';
      }
      b.disabled = !v;
      b.classList.toggle('is-unavailable', !v);
    });
  }

  pickSize(btn, fromCar) {
    this.sizeChosen = true;   // klik albo dobór po modelu auta = wybór klienta
    this.sizeButtons.forEach((b) => b.classList.toggle('is-selected', b === btn));
    if (this.variants.length) {
      this.selectedSize = btn.dataset.key || '';
      // zdjęcie może zależeć od rozmiaru (np. Ero Soft L) — synchronizuj też obraz
      this.syncSelection();
    } else if (btn.dataset.variant) {
      this.selectedVariant = btn.dataset.variant;
    }
    if (!fromCar) {
      const hint = btn.dataset.hint;
      if (hint) this.showNote(BB_EN ? `Size <b>${escapeHtml(btn.dataset.key)}</b> fits: ${escapeHtml(hint)}.` : `Rozmiar <b>${escapeHtml(btn.dataset.key)}</b> pasuje do: ${escapeHtml(hint)}.`);
    }
  }

  onCar() {
    const v = this.carInput.value.trim();
    if (v.length < 2) { this.hideNote(); return; }
    const found = matchCar(v);
    const sz = found.size;
    /* uczciwość ponad domykanie sprzedaży: gdy właściwy rozmiar jest wyprzedany,
       NIE podmieniamy rekomendacji na inny (klient kupiłby złą matę) — mówimy
       wprost, że pasujący rozmiar jest chwilowo niedostępny */
    const avail = this.sizeButtons.filter((b) => !b.disabled).map((b) => (b.dataset.key || '').toUpperCase());
    const btn = this.sizeButtons.find((b) => (b.dataset.key || '').toUpperCase() === sz);
    if (found.matched && !avail.includes(sz)) {
      this.showNote(BB_EN ? `<b>${escapeHtml(v)}</b> takes size <b>${sz}</b> — currently sold out. Check back soon or message us.` : `Do <b>${escapeHtml(v)}</b> pasuje rozmiar <b>${sz}</b> — chwilowo wyprzedany. Zajrzyj wkrótce albo napisz do nas.`);
      return;
    }
    if (btn) this.pickSize(btn, true);
    if (found.matched) {
      this.showNote(BB_EN ? `For <b>${escapeHtml(v)}</b> we recommend size <b>${sz}</b> — we selected it for you.` : `Dla <b>${escapeHtml(v)}</b> polecamy rozmiar <b>${sz}</b> — zaznaczyliśmy go za Ciebie.`);
    } else {
      this.showNote(BB_EN ? "We didn't recognise that model — <b>M</b> fits most cars. Message us and we'll size it for sure." : 'Nie rozpoznaliśmy modelu — najczęściej pasuje <b>M</b>. Napisz do nas, dobierzemy pewnie.');
    }
  }

  showNote(html) {
    if (!this.note || !this.noteText) return;
    this.noteText.innerHTML = html;
    this.note.classList.add('is-shown');
  }

  hideNote() {
    this.note?.classList.remove('is-shown');
  }

  toggleAddon(a) {
    const on = a.getAttribute('aria-pressed') === 'true';
    a.setAttribute('aria-pressed', String(!on));
    a.classList.toggle('is-on', !on);
    this.recalc();
  }

  recalc() {
    let total = this.baseCents;
    const floorComp = this.floorCompPick();
    if (floorComp && this.floorWanted() && this.floorAllowed()) {
      total += floorComp.price;
    }
    if (this.floorAddonSel) {
      const m = this.bundleMattressCents && this.bundleMattressCents < this.floorAddonSel.price
        ? this.bundleMattressCents : this.floorAddonSel.price;
      total += m;
    }
    this.addons.forEach((a) => {
      if (a.getAttribute('aria-pressed') === 'true') total += parseInt(a.dataset.price || '0', 10);
    });
    const txt = formatMoney(total, this.moneyFormat);
    this.totalEls.forEach((el) => { el.textContent = txt; });
    /* przekreślona cena przy przycisku: suma compare_at - cena linii zestawu
       faktycznie w koszyku (materac + podłoga w rozmiarze maty). Liczona z
       wariantów, nie z ustawień sekcji - zmiana ceny w Adminie sama przepływa. */
    const cmpEl = this.querySelector('[data-spotlight-btn-compare]');
    if (cmpEl) {
      let saving = 0;
      if (this.floorAddonSel) {
        saving += Math.max(0, (this.floorAddonSel.compare || 0) - this.floorAddonSel.price);
      }
      if (floorComp && this.floorWanted() && this.floorAllowed()) {
        saving += Math.max(0, (floorComp.compare || 0) - floorComp.price);
      }
      cmpEl.hidden = saving <= 0;
      cmpEl.textContent = saving > 0 ? formatMoney(total + saving, this.moneyFormat) : '';
    }
    this.syncExpress();
    this.syncBuyState();
    this.syncShipNote();
  }

  /* Portfel ekspresowy kupuje tylko wariant z formularza — synchronizuj id
     i chowaj przycisk, gdy w koszyku wylądowałoby więcej pozycji (pakiet/dodatki). */
  syncExpress() {
    if (!this.expressWrap) return;
    const multi = !!this.floorAddonSel ||
      !!(this.floorCompPick() && this.floorWanted() && this.floorAllowed()) ||
      this.addons.some((a) => a.getAttribute('aria-pressed') === 'true' && a.dataset.variant);
    if (this.expressId && this.selectedVariant) this.expressId.value = this.selectedVariant;
    /* also hidden for an unavailable variant: the wallet would post the same id and fail */
    this.expressWrap.hidden = multi || !this.selectedVariant || this.selectedAvailable === false;
  }

  /* Compose the intent for this configuration, then hand it to the single
     writer. addBundle() no longer touches the cart itself - everything goes
     through cartAdd(), so exactly one thing mutates the cart. */
  async addBundle() {
    /* the button is disabled in this state; guard the programmatic path too */
    if (this.selectedAvailable === false) { this.flash(this.addBtn); return; }
    const group = bbGroupId();
    const parentLabel = this.dataset.productName
      || this.querySelector('[data-spotlight-name]')?.textContent?.trim()
      || '';
    const items = [];

    if (this.selectedVariant) {
      const props = {
        [BB_GROUP]: group,
        [BB_ROLE]: 'parent',
        [BB_KIND]: this.dataset.bbKind || 'dengu',
      };
      /* przedsprzedaż: widoczna właściwość (bez "_") na linii maty - koszyk, szuflada, checkout.
         Reconciler (bebobi-cart-bundles.js) dopasowuje po variant_id i _bb*, nie po tej właściwości. */
      const preName = this.addBtn?.dataset.preorderPropName;
      const preValue = this.addBtn?.dataset.preorderPropValue;
      if (this.selectedPreorder && preName && preValue) props[preName] = preValue;
      items.push({
        id: Number(this.selectedVariant),
        quantity: 1,
        properties: props,
      });
    }

    /* mattress (KUKO+) - same slot mechanism, different group key */
    if (this.floorAddonSel) {
      items.push(this.componentItem(this.floorAddonSel.id, 'materac', group, parentLabel));
    }

    /* rigid floor as its own line: W komplecie tier, matched to the mat size,
       and never without the mattress it slides into.

       The warehouse packs 3 BOARDS per floor, so the line carries 3 per mat
       (_bbPer = boards_per_set) and the bebobi-packin-free function zeroes 2 of
       every 3 - the customer still pays one board's price per set. Passing the
       count through _bbPer means the existing component reconciler produces it,
       rather than a second rule fighting it for the same line. */
    const floorComp = this.floorCompPick();
    if (floorComp && this.floorWanted() && this.floorAllowed()) {
      /* The board count rides on the variant data this page already rendered.
         window.bbBoardsPerSet is CART-scoped, so on a product page with an
         empty cart it is empty and every floor would be added at quantity 1 -
         which is exactly the bug this replaced. */
      const boards = Number(floorComp.boards) || 1;
      items.push(this.componentItem(floorComp.id, 'sztywna', group, parentLabel, boards));
    }

    this.addons.forEach((a) => {
      if (a.getAttribute('aria-pressed') === 'true' && a.dataset.variant) {
        items.push(this.componentItem(a.dataset.variant, a.dataset.slot || 'dodatek', group, parentLabel));
      }
    });

    if (!items.length) { this.flash(this.addBtn); return; }
    await this.cartAdd(items, this.addBtn);
  }

  componentItem(variantId, slot, group, parentLabel, per = 1) {
    const props = {
      [BB_GROUP]: group,
      [BB_ROLE]: 'component',
      [BB_SLOT]: slot,
      [BB_PER]: String(per),        // units per parent; 3 for the rigid floor
    };
    if (parentLabel) props[BB_PARENT_LABEL] = parentLabel;
    return { id: Number(variantId), quantity: per, properties: props };
  }

  /* The physical count lives on the tracked "Osobno" sibling; the untracked
     W komplecie twins always read available=true. A tiered candidate is only
     offerable while the variant that carries the real stock is in stock. */
  bbSiblingOk(list, v, eq) {
    if (!v || !v.zestaw) return true;               // no price tiers on this product
    const sib = list.find((x) => x !== v
      && x.zestaw && !this.bbTier(x.zestaw)
      && eq(x.rozmiar || '', v.rozmiar || '')
      && eq(x.kolor || '', v.kolor || ''));
    return !sib || sib.available !== false;
  }

  /* Mattress candidates for the selected mat size: W komplecie tier, in stock
     (via the Osobno sibling). Shared by the colour picker and the floor gate. */
  bbMattressCandidates(eq) {
    if (!this.floorAddon.length) return null;       // product has no mattress add-on
    const sz = String(this.selectedSize || '').toUpperCase();
    const wantSize = sz === 'S' ? 'S' : 'M/L';
    return this.floorAddon.filter((v) => eq(v.rozmiar, wantSize)
      && (!v.zestaw || this.bbTier(v.zestaw))
      && v.available !== false
      && this.bbSiblingOk(this.floorAddon, v, eq));
  }

  /* The floor component variant for the current configuration: prefer the
     "W komplecie" price tier, match the mat size (S -> S, M i L -> M/L).
     Without variant data (Dengu) it is the single configured variant. */
  floorCompPick() {
    const eq = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
    let list = this.floorComps;
    if (!list.length) {
      return this.floorComponentId
        ? { id: this.floorComponentId, price: parseInt(this.dataset.floorComponentPrice || '0', 10) }
        : null;
    }
    if (list.some((v) => v.zestaw)) list = list.filter((v) => this.bbTier(v.zestaw));
    if (list.some((v) => v.rozmiar)) {
      const want = String(this.selectedSize || '').trim().toUpperCase() === 'S' ? 'S' : 'M/L';
      list = list.filter((v) => eq(v.rozmiar, want));
    }
    /* no fallback to an unavailable variant: a sold-out floor is not offered */
    return list.find((v) => v.available !== false && this.bbSiblingOk(this.floorComps, v, eq)) || null;
  }

  /* "W komplecie" tier — PL i EN ("Bundled"): sekcja renderuje przetłumaczone
     wartości opcji na /en, więc dopasowanie zna oba literały */
  bbTier(v) {
    return /w\s*komplecie|bundled/i.test(String(v || ''));
  }

  /* Presta rule (depends_on): the rigid board slides into the mattress, so
     where a mattress add-on is configured the floor ships only with it. */
  floorAllowed() {
    return !this.floorAddon.length || !!this.floorAddonSel;
  }

  /* Is the rigid floor selected? After the floor option is retired the mat has
     a single variant and the choice lives on a toggle; while the option still
     exists we fall back to the selected variant's floor value. */
  floorWanted() {
    if (this.floorSelect) return this.floorSelect.value === 'rigid';
    return /twarda/i.test(String(this.selectedFloor || ''));
  }

  /* Hide the rigid-floor offer entirely when it cannot be fulfilled: the
     size-matched floor component, or - on KUKO+ - every mattress colour for
     this size, is out of stock. Availability is page-load data, so this only
     needs to run on selection changes. */
  syncFloorGate() {
    if (!this.floorSelect) return;
    const eq = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
    const cands = this.bbMattressCandidates(eq);
    const ok = !!this.floorCompPick() && (cands === null || cands.length > 0);
    const rigid = [...this.floorSelect.options].find((o) => o.value === 'rigid');
    if (rigid) { rigid.disabled = !ok; rigid.hidden = !ok; }
    const block = this.floorSelect.closest('.bebobi-floor');
    if (block) block.hidden = !ok;                  // a one-option select is noise
    if (!ok && this.floorSelect.value === 'rigid') {
      this.floorSelect.value = 'standard';
      this.syncFloorSelect();
    }
  }

  /* Reflect the choice in the control itself and show the separate-line note
     only when it applies. */
  syncFloorSelect() {
    const rigid = this.floorWanted();
    this.floorSelect?.closest('.bebobi-floor__select-row')?.classList.toggle('is-rigid', rigid);
    this.floorChoiceBtns?.forEach((b) => {
      b.classList.toggle('is-selected', (b.dataset.floorChoice === 'rigid') === rigid);
    });
    if (this.floorNote) this.floorNote.hidden = !rigid;
    this.syncFloorAddon();
    this.recalc();
  }

  async addCross(btn) {
    if (btn.dataset.variant) {
      /* cross-sell is a standalone purchase - deliberately no _bb* properties,
         so the reconciler never treats it as part of a bundle */
      await this.cartAdd([{ id: Number(btn.dataset.variant), quantity: 1 }], btn);
    }
    this.markAdded(btn);
  }

  markAdded(btn) {
    const label = btn.dataset.labelAdded || 'Dodano';
    const orig = btn.dataset.origLabel || btn.textContent;
    btn.dataset.origLabel = orig;
    btn.classList.add('is-added');
    btn.textContent = `${label} ✓`;
    clearTimeout(btn._t);
    btn._t = setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = orig; }, 1400);
  }

  /* ---- the single serialized cart writer ---------------------------------
     Every mutation in this element goes through here, queued on one promise
     chain. /cart/change.js handles one line at a time, so a multi-line edit is
     several sequential requests; serializing them stops this element racing
     against itself. */
  enqueue(task) {
    this._chain = (this._chain || Promise.resolve()).then(task, task);
    return this._chain;
  }

  get routes() {
    const r = (window.Theme && window.Theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: r.cart_change_url || '/cart/change.js',
      cart: (r.cart_url || '/cart') + '.js',
    };
  }

  sectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  async fetchCart() {
    const res = await fetch(this.routes.cart, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  async cartAdd(items, sourceBtn) {
    return this.enqueue(async () => {
      try {
        const res = await fetch(this.routes.add, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: this.sectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        });
        if (!res.ok) {
          /* 422 = a variant is unavailable. The parent may still have landed,
             so reconcile rather than leaving an orphaned component behind. */
          const body = await res.text().catch(() => '');
          throw new Error(`cart/add ${res.status} ${body.slice(0, 200)}`);
        }
        const data = await res.json();
        /* Reconciliation is bebobi-cart-bundles.js's job - it listens for this
           event. Doing it here too would put two writers on one cart. */
        let cart = null;
        try { cart = await this.fetchCart(); } catch (_) { /* non-fatal */ }
        this.announce(cart, data.sections);
      } catch (err) {
        console.error('[bebobi-spotlight] add to cart failed', err);
        this.flash(sourceBtn);
      }
    });
  }

  announce(cart, sections) {
    document.dispatchEvent(
      new CartAddEvent(cart || {}, this.id || 'bebobi-spotlight', {
        /* deliberately NOT BB_SOURCE: the global reconciler must treat this as
           someone else's write and reconcile the components we just added */
        source: 'bebobi-spotlight',
        itemCount: cart && cart.item_count,
        sections,
      })
    );
  }

  flash(el) {
    if (!el || typeof el.animate !== 'function') return;
    el.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(0.97)' }, { transform: 'scale(1)' }],
      { duration: 200 }
    );
  }
}

if (!customElements.get('bebobi-spotlight')) {
  customElements.define('bebobi-spotlight', BebobiSpotlight);
}
